import { useState, useEffect, useCallback, useRef } from 'react';
import { MIDIMessageData, ActiveChord, AudioSettings } from '../types';
import { startLiveNote, stopLiveNote, ActiveSound, formatChordNotatorChord } from '../utils/audio';
import { notesToChordString } from '../utils/midi';

export interface UseMidiHandlersReturn {
  midiInputs: MIDIInput[];
  selectedMidiInputId: string;
  setSelectedMidiInputId: React.Dispatch<React.SetStateAction<string>>;
  lastMidiMessage: MIDIMessageData | null;
}

export function useMidiHandlers(
  activeChord: ActiveChord | null,
  updateSourceChart: (updater: (prev: any[]) => any[]) => void,
  audioSettings: AudioSettings,
): UseMidiHandlersReturn {
  const [midiAccess, setMidiAccess] = useState<MIDIAccess | null>(null);
  const [midiInputs, setMidiInputs] = useState<MIDIInput[]>([]);
  const [selectedMidiInputId, setSelectedMidiInputId] = useState<string>('');
  const [lastMidiMessage, setLastMidiMessage] = useState<MIDIMessageData | null>(null);
  const pressedMidiNotesRef = useRef<Set<number>>(new Set());
  const lastPlayedNotesRef = useRef<Set<number>>(new Set());
  const chordRecognitionTimeoutRef = useRef<number | null>(null);
  const activeMidiSoundsRef = useRef<Map<number, ActiveSound>>(new Map());

  // Initialize MIDI Access
  useEffect(() => {
    const initMidi = async () => {
      if (navigator.requestMIDIAccess) {
        try {
          const access = await navigator.requestMIDIAccess({ sysex: false });
          setMidiAccess(access);
        } catch (error) {
          console.error("MIDI Access Denied or not available.", error);
        }
      } else {
        console.warn("Web MIDI API not supported in this browser.");
      }
    };
    initMidi();
  }, []);

  // Handle MIDI device connections and state changes
  useEffect(() => {
    if (!midiAccess) return;
    const updateDevices = () => {
      const inputs: MIDIInput[] = Array.from(midiAccess.inputs.values());
      setMidiInputs(inputs);
      if (!selectedMidiInputId && inputs.length > 0 && midiInputs.length === 0) {
        setSelectedMidiInputId(inputs[0].id);
      }
    };
    updateDevices();
    midiAccess.onstatechange = updateDevices;
    return () => {
      if (midiAccess) {
        midiAccess.onstatechange = null;
      }
    };
  }, [midiAccess, selectedMidiInputId, midiInputs.length]);

  const handleMidiMessage = useCallback((event: MIDIMessageEvent) => {
    if (!event.data) return;
    const [command, note, velocity] = Array.from(event.data);
    const NOTE_ON = 0x90;
    const NOTE_OFF = 0x80;
    const commandType = command & 0xF0;
    const channel = (command & 0x0F) + 1;
    const isNoteOn = commandType === NOTE_ON && velocity > 0;
    const isNoteOff = commandType === NOTE_OFF || (commandType === NOTE_ON && velocity === 0);

    const midiNoteToName = (midiNote: number): string => {
      const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const octave = Math.floor(midiNote / 12) - 1;
      return `${noteNames[midiNote % 12]}${octave}`;
    };

    setLastMidiMessage({
      command: commandType,
      note,
      velocity,
      channel,
      type: isNoteOn ? 'Note On' : 'Note Off',
      noteName: midiNoteToName(note),
    });

    if (isNoteOn) {
      if (activeMidiSoundsRef.current.has(note)) {
        stopLiveNote(activeMidiSoundsRef.current.get(note)!, audioSettings);
        activeMidiSoundsRef.current.delete(note);
      }
      const sound = startLiveNote(note, velocity, audioSettings);
      if (sound) {
        activeMidiSoundsRef.current.set(note, sound);
      }
    } else if (isNoteOff) {
      if (activeMidiSoundsRef.current.has(note)) {
        stopLiveNote(activeMidiSoundsRef.current.get(note)!, audioSettings);
        activeMidiSoundsRef.current.delete(note);
      }
    }

    if (chordRecognitionTimeoutRef.current) {
      clearTimeout(chordRecognitionTimeoutRef.current);
    }

    if (isNoteOn) {
      pressedMidiNotesRef.current.add(note);
      lastPlayedNotesRef.current = new Set(pressedMidiNotesRef.current);
    } else if (isNoteOff) {
      pressedMidiNotesRef.current.delete(note);
    }

    if (pressedMidiNotesRef.current.size === 0 && lastPlayedNotesRef.current.size > 0) {
      const notesToRecognize = [...lastPlayedNotesRef.current];
      lastPlayedNotesRef.current.clear();

      chordRecognitionTimeoutRef.current = Number(window.setTimeout(() => {
        if (activeChord && notesToRecognize.length > 0) {
          const recognizedChord = notesToChordString(notesToRecognize);

          if (recognizedChord) {
            const { measureIndex, chordIndex } = activeChord;
            updateSourceChart(prevChart => {
              const newChart = JSON.parse(JSON.stringify(prevChart));
              const finalChord = formatChordNotatorChord(recognizedChord);
              newChart[measureIndex].chords[chordIndex] = finalChord;
              return newChart;
            });
          }
        }
      }, 50));
    }
  }, [activeChord, updateSourceChart, audioSettings]);

  // Attach/detach MIDI input listener
  useEffect(() => {
    const selectedInput = midiInputs.find(input => input.id === selectedMidiInputId);

    midiInputs.forEach(input => {
      input.removeEventListener('midimessage', handleMidiMessage);
    });

    if (selectedInput && selectedMidiInputId) {
      selectedInput.open();
      selectedInput.addEventListener('midimessage', handleMidiMessage);
    }

    return () => {
      if (selectedInput) {
        selectedInput.removeEventListener('midimessage', handleMidiMessage);
      }
    };
  }, [selectedMidiInputId, midiInputs, handleMidiMessage]);

  return {
    midiInputs,
    selectedMidiInputId,
    setSelectedMidiInputId,
    lastMidiMessage,
  };
}
